"use client";

import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useGrn, useGrnMutations, type GrnItemRow } from "@/hooks/api/useGrns";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

function varianceBadge(pct: number) {
  if (Math.abs(pct) < 0.05) {
    return null;
  }
  const bad = Math.abs(pct) > 5;

  return (
    <Badge variant={bad ? "destructive" : "secondary"} className="ml-1 text-[10px]">
      Δ {pct.toFixed(1)}%
    </Badge>
  );
}

export default function GrnDetailPage() {
  const params = useParams();
  const id = Number(params.id);
  const { data: grn, isLoading, refetch } = useGrn(Number.isFinite(id) ? id : null);
  const { update, complete } = useGrnMutations();
  const [items, setItems] = useState<GrnItemRow[] | null>(null);

  const displayItems = items ?? grn?.items ?? [];

  useEffect(() => {
    if (grn?.items) {
      setItems(grn.items.map((x) => ({ ...x })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when GRN identity/status changes only
  }, [grn?.id, grn?.status]);

  if (!Number.isFinite(id)) {
    return <p className="p-4 text-sm text-destructive">Invalid id</p>;
  }

  async function saveDraft() {
    if (!grn || !items) {
      return;
    }
    await update.mutateAsync({
      id: grn.id,
      body: {
        items: items.map((it) => ({
          product_id: it.product_id,
          qty_ordered: it.qty_ordered,
          qty_received: it.qty_received,
          rate: it.rate,
          gst_rate: it.gst_rate,
          batch_no: it.batch_no,
          mfg_date: it.mfg_date,
          expiry_date: it.expiry_date,
        })),
      },
    });
    void refetch();
  }

  async function doComplete() {
    if (!grn) {
      return;
    }
    if (items) {
      await saveDraft();
    }
    await complete.mutateAsync(grn.id);
    void refetch();
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4">
      <Link
        to="/purchase/grn"
        className={cn(buttonVariants({ variant: "outline" }), "inline-flex h-9 items-center px-3")}
      >
        ← GRNs
      </Link>
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {grn && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h1 className="font-mono text-xl font-semibold">{grn.grn_no}</h1>
              <p className="text-sm text-muted-foreground">
                {grn.grn_date} · Supplier #{grn.supplier_id}{" "}
                <Badge variant={grn.status === "completed" ? "default" : "secondary"}>{grn.status}</Badge>
              </p>
            </div>
            {grn.status === "draft" && (
              <div className="flex gap-2">
                <Button variant="outline" disabled={update.isPending} onClick={() => void saveDraft()}>
                  Save
                </Button>
                <Button disabled={complete.isPending} onClick={() => void doComplete()}>
                  Complete GRN
                </Button>
              </div>
            )}
          </div>
          <Card className="overflow-x-auto p-4">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-2">Product</th>
                  <th>Ordered</th>
                  <th>Received</th>
                  <th>Rate</th>
                  <th>Batch</th>
                  <th>Expiry</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {displayItems.map((it, idx) => (
                  <tr key={it.id} className="border-b border-border/50">
                    <td className="py-2 pr-2 font-medium">{it.product_name}</td>
                    <td>{it.qty_ordered}</td>
                    <td>
                      <Input
                        className="h-8 w-20 font-mono"
                        disabled={grn.status !== "draft"}
                        type="number"
                        value={it.qty_received}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          if (!Number.isFinite(v)) {
                            return;
                          }
                          setItems((prev) => {
                            const base = prev ?? grn.items;
                            const n = base.map((x) => ({ ...x }));

                            n[idx] = { ...n[idx], qty_received: v };

                            return n;
                          });
                        }}
                      />
                    </td>
                    <td>
                      <Input
                        className="h-8 w-20 font-mono"
                        disabled={grn.status !== "draft"}
                        type="number"
                        value={it.rate}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          if (!Number.isFinite(v)) {
                            return;
                          }
                          setItems((prev) => {
                            const base = prev ?? grn.items;
                            const n = base.map((x) => ({ ...x }));

                            n[idx] = { ...n[idx], rate: v };

                            return n;
                          });
                        }}
                      />
                    </td>
                    <td>
                      <Input
                        className="h-8 w-24"
                        disabled={grn.status !== "draft"}
                        value={it.batch_no ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setItems((prev) => {
                            const base = prev ?? grn.items;
                            const n = base.map((x) => ({ ...x }));

                            n[idx] = { ...n[idx], batch_no: v };

                            return n;
                          });
                        }}
                      />
                    </td>
                    <td>
                      <Input
                        className="h-8 w-32"
                        type="date"
                        disabled={grn.status !== "draft"}
                        value={it.expiry_date ? String(it.expiry_date).slice(0, 10) : ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setItems((prev) => {
                            const base = prev ?? grn.items;
                            const n = base.map((x) => ({ ...x }));

                            n[idx] = { ...n[idx], expiry_date: v || null };

                            return n;
                          });
                        }}
                      />
                    </td>
                    <td className={cn("text-right")}>{varianceBadge(it.price_variance_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
